import { DocumentationGroupResult, GroupedDocumentationItem } from "@/shared/documentation/documentation.types.ts";
import { GetDataDisplayPropertiesByConfig } from "@/shared/data-display-config/composable/data-display-config.ts";
import { ContentNode, mapContentNodeToDocumentationBase } from "@/shared/documentation/mapper/content-node.mapper.ts";
import { ContentEdge, ContentGroup } from "@/shared/services/graphql/generated/consumer-graph-types.ts";

/**
 * One group of a `contentsGrouped`/`relatedContentsGrouped` result. It carries no hits: the group list
 * query does not select any - they are fetched lazily once the group gets expanded and are passed to the
 * mapper separately.
 *
 * The schema type is used rather than an operation type, because the queries are assembled at runtime
 * from the search context and the display config (see `generateContentsGroupedQuery`).
 */
export type ContentGroupResult = Omit<ContentGroup, "hits">;

const mapHitToGroupedDocumentationItem = (
    hit: ContentEdge,
    getDataDisplayPropertiesByConfig?: GetDataDisplayPropertiesByConfig
): GroupedDocumentationItem => {
    const node = hit.node as ContentNode;

    return mapContentNodeToDocumentationBase(node, hit.cursor, node.locale ?? "", getDataDisplayPropertiesByConfig);
};

/**
 * Maps the raw `contentsGrouped` GraphQL groups into the view-model shape (`DocumentationGroupResult[]`)
 * consumed by the documentation grouped list components.
 *
 * @param groups - The groups of the current `contentsGrouped` result.
 * @param hitsByGroupCursor - The lazily loaded hits of every already expanded group, keyed by group cursor.
 * @param getDataDisplayPropertiesByConfig - Maps a content node to the property items of the display config.
 */
export const mapContentGroupsToGroupResult = (
    groups: ContentGroupResult[],
    hitsByGroupCursor: Record<string, ContentEdge[]>,
    getDataDisplayPropertiesByConfig?: GetDataDisplayPropertiesByConfig
): DocumentationGroupResult[] => {
    const findKeyEntry = (group: ContentGroupResult, field: string) => group.key.find((entry) => entry.field === field);

    return groups.map((group) => {
        const mimeTypeEntry = findKeyEntry(group, "mimeType");

        return {
            group: {
                contentBaseIds: findKeyEntry(group, "contentBaseIds")?.value ?? "",
                title: group.teaser?.title ?? "",
                mimeType: mimeTypeEntry?.teasers?.title ?? mimeTypeEntry?.value ?? "",
                language: findKeyEntry(group, "language")?.value ?? "",
                itemsTotal: { count: group.total.count, countRelation: group.total.countRelation ?? undefined },
            },
            items: (hitsByGroupCursor[group.cursor] ?? []).map((hit) =>
                mapHitToGroupedDocumentationItem(hit, getDataDisplayPropertiesByConfig)
            ),
            cursor: group.cursor,
        };
    });
};
